import {
  activateDeviceDocument,
  assignDeviceDocument,
  bulkAssignDeviceDocument,
  listDeviceDocumentAssignments,
  listDeviceDocumentMachines,
  listDeviceDocuments,
  processDeviceDocument,
  unassignDeviceDocument,
  uploadDeviceDocument,
} from '../adapters/DeviceDocuments/DeviceDocumentsAdapter';
import { getMachineDetails } from './MachineDetailsHelper';
import { get_NX5_RegistrationDetails } from './NX5/NX5_RegistrationDetailsHelper';

export const DEFAULT_DEVICE_MODELS = ['V730i', 'NX5'];
export const DEFAULT_DOCUMENT_TYPE = 'user_manual';
export const DEFAULT_LANGUAGE = 'en';
export const MAX_MANUAL_BYTES = 100 * 1024 * 1024;
export const PROCESS_POLL_MS = 3000;

export class DeviceDocumentsApiError extends Error {
  constructor(message, { status, data } = {}) {
    super(message);
    this.name = 'DeviceDocumentsApiError';
    this.status = status;
    this.data = data;
  }
}

const unwrap = (res) => res?.data?.data ?? res?.data ?? {};

const readErrorMessage = (payload, fallback) =>
  payload?.data?.message ||
  payload?.message ||
  payload?.error?.message ||
  payload?.data?.error ||
  fallback;

export const getDeviceDocumentsErrorMessage = (err, fallback = 'Request failed') => {
  if (err instanceof DeviceDocumentsApiError) return err.message;
  return readErrorMessage(err?.response?.data, err?.message || fallback);
};

export const getDeviceDocumentsErrorStatus = (err) =>
  err instanceof DeviceDocumentsApiError
    ? err.status
    : err?.response?.status;

const toApiError = (err, fallback) => {
  if (err instanceof DeviceDocumentsApiError) return err;
  const status = err?.response?.status;
  const data = err?.response?.data?.data ?? err?.response?.data;
  return new DeviceDocumentsApiError(getDeviceDocumentsErrorMessage(err, fallback), {
    status,
    data,
  });
};

const requestEnvelope = (request, fallback) =>
  new Promise((resolve, reject) => {
    request
      .then((res) => {
        const http = res?.status;
        if (http >= 200 && http < 300) {
          resolve(unwrap(res));
          return;
        }
        reject(new DeviceDocumentsApiError(readErrorMessage(res?.data, fallback), {
          status: http,
          data: unwrap(res),
        }));
      })
      .catch((err) => reject(toApiError(err, fallback)));
  });

export const normalizeMachineUDI = (value) =>
  String(value || '')
    .trim()
    .replace(/-iomt$/i, '');

export const padLastFourDigits = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.slice(-4).padStart(4, '0');
};

export const getUdiLastFourDigits = (machineUDI) => {
  const normalized = normalizeMachineUDI(machineUDI);
  if (!normalized) return '';
  const lastPart = normalized.split('-').filter(Boolean).pop() || normalized;
  const afterPrefix = lastPart.replace(/^[0-9]*[A-Za-z]+/, '');
  const digits = String(afterPrefix || lastPart).replace(/\D/g, '');
  if (!digits) return '';
  return digits.slice(-4).padStart(4, '0');
};

export const matchesLastFourDigits = (machineUDI, query) => {
  const needle = padLastFourDigits(query);
  if (!needle) return false;
  return getUdiLastFourDigits(machineUDI) === needle;
};

export const getMachineShortSerial = (machineUDI) => {
  const normalized = normalizeMachineUDI(machineUDI);
  if (!normalized) return '';
  const parts = normalized.split('-').filter(Boolean);
  return parts.find((part) => /1g/i.test(part)) || parts[parts.length - 1] || normalized;
};

export const filterMachinesBySerialQuery = (list, query) => {
  const q = String(query || '').trim();
  if (!q) return list;
  const lower = q.toLowerCase();
  const digitsOnly = /^\d+$/.test(q);
  return list.filter((item) => {
    const udi = String(item?.machineUDI || '');
    const short = getMachineShortSerial(udi).toLowerCase();
    if (!digitsOnly) {
      return udi.toLowerCase().includes(lower) || short.includes(lower);
    }
    if (matchesLastFourDigits(udi, q)) return true;
    return short.startsWith(lower);
  });
};

const unwrapMachineList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.machines)) return payload.machines;
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(payload?.data?.machines)) return payload.data.machines;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data?.records)) return payload.data.records;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const readMachineUdi = (item) =>
  normalizeMachineUDI(
    item?.machineUDI
    || item?.machine_udi
    || item?.SerialNo
    || item?.serialNo
    || item?.Serial_No_
    || item?.udi
  );

const dedupeMachines = (rows) => {
  const seen = new Set();
  return rows.filter((row) => {
    if (!row.machineUDI || seen.has(row.machineUDI)) return false;
    seen.add(row.machineUDI);
    return true;
  });
};

const fetchHospitalMachines = (deviceModel) => {
  const loadV730i = !deviceModel || deviceModel === 'V730i'
    ? getMachineDetails().then(unwrapMachineList).catch(() => [])
    : Promise.resolve([]);
  const loadNx5 = !deviceModel || deviceModel === 'NX5'
    ? get_NX5_RegistrationDetails().then(unwrapMachineList).catch(() => [])
    : Promise.resolve([]);

  return Promise.all([loadV730i, loadNx5]).then(([v730i, nx5]) => dedupeMachines([
    ...v730i.map((item) => ({ machineUDI: readMachineUdi(item), deviceModel: 'V730i' })),
    ...nx5.map((item) => ({ machineUDI: readMachineUdi(item), deviceModel: 'NX5' })),
  ]));
};

export const fetchRegisteredMachines = (deviceModel) =>
  requestEnvelope(
    listDeviceDocumentMachines(deviceModel ? { deviceModel } : {}),
    'Unable to load machines'
  )
    .then((data) => dedupeMachines(
      unwrapMachineList(data).map((item) => ({
        machineUDI: readMachineUdi(item),
        deviceModel: item?.deviceModel || deviceModel || '',
      }))
    ))
    .then((list) => (list.length ? list : fetchHospitalMachines(deviceModel)))
    .catch(() => fetchHospitalMachines(deviceModel));

export const isReadyDocument = (doc) =>
  String(doc?.processingStatus || '').toLowerCase() === 'ready';

export const isActiveDocument = (doc) =>
  String(doc?.status || '').toLowerCase() === 'active';

export const formatDocumentLabel = (doc) => {
  if (!doc) return '';
  const title = doc.title || 'User Manual';
  const version = doc.version ? ` ${doc.version}` : '';
  return `${title}${version}`.trim();
};

export const getAssignmentSerial = (row) =>
  row?.machineUDI
  || row?.machine_udi
  || row?.machineSerial
  || row?.serialNo
  || row?.SerialNo
  || row?.udi
  || '';

export const getAssignmentModel = (row) =>
  row?.deviceModel || row?.document?.deviceModel || '';

export const getAssignmentVersion = (row) =>
  row?.document?.version || row?.version || '';

export const getAssignmentDate = (row) =>
  row?.assignedAt || row?.assigned_at || row?.updatedAt || row?.createdAt || '';

export const fetchDeviceDocuments = (params = {}) =>
  requestEnvelope(listDeviceDocuments(params), 'Unable to load manuals');

export const uploadUserManual = ({ file, deviceModel, version, title, documentType = DEFAULT_DOCUMENT_TYPE, language = DEFAULT_LANGUAGE }) => {
  const body = new FormData();
  body.append('file', file);
  body.append('deviceModel', deviceModel);
  body.append('version', version);
  body.append('title', title);
  body.append('documentType', documentType);
  body.append('language', language);
  return requestEnvelope(uploadDeviceDocument(body), 'Unable to upload manual');
};

export const processUserManual = (documentId) =>
  requestEnvelope(processDeviceDocument(documentId), 'Unable to start processing');

export const setDocumentStatus = (documentId, status) =>
  requestEnvelope(activateDeviceDocument(documentId, status), 'Unable to update default');

export const fetchDocumentAssignments = (params = {}) =>
  requestEnvelope(listDeviceDocumentAssignments(params), 'Unable to load assignments');

export const assignManualToMachine = ({ machineUDI, documentId }) =>
  requestEnvelope(
    assignDeviceDocument({
      machineUDI: normalizeMachineUDI(machineUDI),
      documentId,
    }),
    'Unable to assign manual'
  );

export const removeManualAssignment = ({ machineUDI }) =>
  requestEnvelope(
    unassignDeviceDocument({ machineUDI: normalizeMachineUDI(machineUDI) }),
    'Unable to remove pin'
  );

export const bulkAssignManual = ({ documentId, machineUDIs, deviceModel }) => {
  const payload = { documentId };
  if (deviceModel) {
    payload.deviceModel = deviceModel;
  } else {
    payload.machineUDIs = (machineUDIs || []).map(normalizeMachineUDI).filter(Boolean);
  }
  return requestEnvelope(bulkAssignDeviceDocument(payload), 'Unable to bulk assign');
};
